import { LoadingStates } from '@pb/utils'
import { createReducer } from 'redux-act'

import { DIGITAL_RETURNS_SESSION_ID_KEY } from '../helpers/constants'
import { returnsBadgeLabel } from '../helpers/labels'
import {
  ReturnsState,
  ReturnsType,
  SubmittedReturnsInfo,
  ReturnsLoginState,
  Notification,
  SetItemAsReturn,
  OrderItemsSuccess,
  UnsetItemFromReturn,
  ReturnsPickupAddress,
  NormalizedOrderItem,
  Labels,
  SetQuantityForReturnItem,
  CustomerInfo,
} from '../types'
import * as actions from './actions'
import initialState from './initialState'
import { getProductTableData } from './normalize'

const returnsOrderValidationInProcess = (state): ReturnsState => ({
  ...state,
  returnsLoginFormStatus: LoadingStates.Loading,
})

const returnsOrderValidationSuccess = (
  state: ReturnsState,
  { orderId, email }: ReturnsLoginState,
): ReturnsState => ({
  ...state,
  orderId,
  email,
  returnsLoginFormStatus: LoadingStates.LoadedSuccess,
})

const returnsOrderValidationReset = (state: ReturnsState): ReturnsState => ({
  ...state,
  returnsLoginFormStatus: LoadingStates.NotLoaded,
})

const setNotification = (state: ReturnsState, notification: Notification): ReturnsState => ({
  ...state,
  notification,
})

const removeNotification = (state: ReturnsState): ReturnsState => ({
  ...state,
  notification: undefined,
})

const returnsOrderValidationFailed = (state: ReturnsState): ReturnsState => ({
  ...state,
  returnsLoginFormStatus: LoadingStates.LoadedFail,
})

const setItemAsReturn = (
  state: ReturnsState,
  {
    data: { productId, itemId, returnReasonCode, returnReasonDesc, comments, quantity },
    labels,
  }: { data: SetItemAsReturn; labels: Labels },
): ReturnsState => ({
  ...state,
  consignments: state.consignments.map((consignment) => ({
    ...consignment,
    returnableItems: consignment.returnableItems.map((item) =>
      item.data?.productId === productId && item.data?.itemId === itemId
        ? {
            ...item,
            isSelected: true,
            badgeText: labels[returnsBadgeLabel],
            tableData: getProductTableData({
              item: item.data,
              quantityToReturn: quantity,
              labels,
            }),
            selectedQuantityValue: quantity,
            returnsInfo: {
              isSelected: true,
              returnType: ReturnsType.RETURN,
              returnReasonCode,
              returnReasonDesc,
              comments,
            },
          }
        : item,
    ),
  })),
})

const unsetItemFromReturn = (
  state: ReturnsState,
  { data: { productId, itemId }, labels }: { data: UnsetItemFromReturn; labels: Labels },
): ReturnsState => ({
  ...state,
  consignments: state.consignments.map((consignment) => ({
    ...consignment,
    returnableItems: consignment.returnableItems.map((item) =>
      item.data?.productId === productId && item.data?.itemId === itemId
        ? {
            ...item,
            badgeText: undefined,
            isSelected: false,
            selectedQuantityValue: undefined,
            tableData: getProductTableData({ item: item.data, labels }),
            returnsInfo: {},
          }
        : item,
    ),
  })),
})

const setQuantityForReturnItem = (
  state: ReturnsState,
  {
    data: { productId, itemId, selectedQuantityValue },
    labels,
  }: { data: SetQuantityForReturnItem; labels: Labels },
): ReturnsState => ({
  ...state,
  consignments: state.consignments.map((consignment) => ({
    ...consignment,
    returnableItems: consignment.returnableItems.map((item) =>
      item.data?.productId === productId && item.data?.itemId === itemId
        ? {
            ...item,
            tableData: getProductTableData({
              item: item.data,
              quantityToReturn: selectedQuantityValue,
              labels,
            }),
            selectedQuantityValue,
            returnsInfo: {
              ...item.returnsInfo,
              returnType: ReturnsType.RETURN,
            },
          }
        : item,
    ),
  })),
})

const orderItemsLoading = (state): ReturnsState => ({
  ...state,
  orderItemsStatus: LoadingStates.Loading,
})

const orderItemsSuccess = (
  state,
  {
    orderId,
    paymentMode,
    consignments,
    customerInfo,
    orderDate,
    currency,
    paymentCardDigits,
    paymentCardProvider,
    invoiceNumber,
    orderStatus,
  }: OrderItemsSuccess,
): ReturnsState => ({
  ...state,
  consignments,
  orderInfo: { createdAt: orderDate, invoiceNumber },
  customerInfo,
  paymentMode,
  currency,
  orderId,
  paymentCardDigits,
  paymentCardProvider,
  orderStatus,
  orderItemsStatus: LoadingStates.LoadedSuccess,
})

const orderItemsFail = (state): ReturnsState => ({
  ...state,
  items: [],
  orderItemsStatus: LoadingStates.LoadedFail,
})

const setReturnsPickupAddress = (
  state: ReturnsState,
  { firstName, lastName, line1, line2, postalCode, city, province }: ReturnsPickupAddress,
): ReturnsState => ({
  ...state,
  customerInfo: {
    ...state.customerInfo,
    returnsPickupAddress: {
      firstName,
      lastName,
      line1,
      line2,
      postalCode,
      city,
      ...(province && { province }),
      isValid: true,
    },
  },
})

const cancelAllItemsFromReturn = (state: ReturnsState): ReturnsState => ({
  ...state,
  consignments: state.consignments.map((consignment) => ({
    ...consignment,
    returnableItems: consignment.returnableItems.map((item) => ({
      ...item,
      badgeText: undefined,
      isSelected: false,
      returnsInfo: {},
    })),
  })),
  customerInfo: {
    ...state.customerInfo,
    returnsPickupAddress: { isValid: true },
  } as CustomerInfo,
})

const submitReturnsInfoLoading = (state: ReturnsState): ReturnsState => ({
  ...state,
  submitReturnsInfoStatus: LoadingStates.Loading,
})

const submitReturnsInfoSuccess = (
  state: ReturnsState,
  submittedReturnsInfo: SubmittedReturnsInfo,
): ReturnsState => ({
  ...state,
  submittedReturnsInfo,
  submitReturnsInfoStatus: LoadingStates.LoadedSuccess,
})

const submitReturnsInfoFail = (state: ReturnsState): ReturnsState => ({
  ...state,
  submitReturnsInfoStatus: LoadingStates.LoadedFail,
})

const cleanReturnsInfo = (state: ReturnsState): ReturnsState => ({
  ...initialState,
  notification: state.notification,
  returnsReasons: state.returnsReasons,
  labelsStatus: state.labelsStatus,
})

const setSelectedItemForEdit = (
  state: ReturnsState,
  selectedItem: NormalizedOrderItem,
): ReturnsState => ({
  ...state,
  selectedItemForEdit: selectedItem,
})

const clearSelectedItemForEdit = (state: ReturnsState): ReturnsState => ({
  ...state,
  selectedItemForEdit: undefined,
})

const setPickupDate = (
  state: ReturnsState,
  pickupDate: ReturnsState['pickupDate'],
): ReturnsState => ({
  ...state,
  pickupDate,
})

const setReturnsReasons = (
  state: ReturnsState,
  returnsReasons: ReturnsState['returnsReasons'],
): ReturnsState => ({
  ...state,
  returnsReasons,
  returnsReasonsStatus: LoadingStates.LoadedSuccess,
})

const returnsReasonsLoading = (state: ReturnsState): ReturnsState => ({
  ...state,
  returnsReasonsStatus: LoadingStates.Loading,
})

const returnsReasonsFail = (state: ReturnsState): ReturnsState => ({
  ...state,
  returnsReasonsStatus: LoadingStates.LoadedFail,
})

const setIsAuthenticated = (state: ReturnsState, sessionId): ReturnsState => {
  sessionStorage.setItem(DIGITAL_RETURNS_SESSION_ID_KEY, sessionId)
  return {
    ...state,
    isAuthenticated: true,
  }
}

const unsetIsAuthenticated = (state: ReturnsState): ReturnsState => {
  sessionStorage.removeItem(DIGITAL_RETURNS_SESSION_ID_KEY)
  return {
    ...state,
    isAuthenticated: false,
  }
}

const setLabelsLoaded = (state: ReturnsState): ReturnsState => ({
  ...state,
  labelsStatus: LoadingStates.LoadedSuccess,
})

const setPickupAddressInvalid = (state: ReturnsState): ReturnsState => ({
  ...state,
  customerInfo: {
    ...state.customerInfo,
    returnsPickupAddress: {
      ...state.customerInfo.returnsPickupAddress,
      isValid: false,
      province: '',
    } as ReturnsPickupAddress,
  },
})

const setPickupAddressValid = (state: ReturnsState): ReturnsState => ({
  ...state,
  customerInfo: {
    ...state.customerInfo,
    returnsPickupAddress: {
      ...state.customerInfo.returnsPickupAddress,
      isValid: true,
    } as ReturnsPickupAddress,
  },
})

export default createReducer((on) => {
  on(actions.returnsOrderValidationInProcess, returnsOrderValidationInProcess)
  on(actions.returnsOrderValidationSuccess, returnsOrderValidationSuccess)
  on(actions.returnsOrderValidationReset, returnsOrderValidationReset)
  on(actions.returnsOrderValidationFailed, returnsOrderValidationFailed)
  on(actions.setNotification, setNotification)
  on(actions.removeNotification, removeNotification)
  on(actions.orderItemsLoading, orderItemsLoading)
  on(actions.orderItemsSuccess, orderItemsSuccess)
  on(actions.orderItemsFail, orderItemsFail)
  on(actions.unsetItemFromReturn, unsetItemFromReturn)
  on(actions.cancelAllItemsFromReturn, cancelAllItemsFromReturn)
  on(actions.setItemAsReturn, setItemAsReturn)
  on(actions.submitReturnsInfoLoading, submitReturnsInfoLoading)
  on(actions.submitReturnsInfoSuccess, submitReturnsInfoSuccess)
  on(actions.submitReturnsInfoFail, submitReturnsInfoFail)
  on(actions.cleanReturnsInfo, cleanReturnsInfo)
  on(actions.setReturnsPickupAddress, setReturnsPickupAddress)
  on(actions.setSelectedItemForEdit, setSelectedItemForEdit)
  on(actions.clearSelectedItemForEdit, clearSelectedItemForEdit)
  on(actions.setPickupDate, setPickupDate)
  on(actions.setReturnsReasons, setReturnsReasons)
  on(actions.returnsReasonsLoading, returnsReasonsLoading)
  on(actions.returnsReasonsFail, returnsReasonsFail)
  on(actions.setIsAuthenticated, setIsAuthenticated)
  on(actions.unsetIsAuthenticated, unsetIsAuthenticated)
  on(actions.setLabelsLoaded, setLabelsLoaded)
  on(actions.setQuantityForReturnItem, setQuantityForReturnItem)
  on(actions.setPickupAddressInvalid, setPickupAddressInvalid)
  on(actions.setPickupAddressValid, setPickupAddressValid)
}, initialState)
